import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import DonateButton from "@/components/DonateButton";
import SanctuaryMasterplan from "@/components/SanctuaryMasterplan";
import { PROJECTS, PILLARS, SANCTUARY_COMPONENTS, IMG } from "@/lib/site";

export default function Programs() {
  return (
    <>
      <section className="bg-forest pb-16 pt-40 text-ivory lg:pb-24 lg:pt-52" data-testid="programs-hero">
        <div className="grain absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="eyebrow mb-6 text-gold">Our Projects</p>
            <h1 className="max-w-3xl text-balance font-display text-4xl font-semibold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl" data-testid="programs-headline">
              Because dignity begins with safety, opportunity and community.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-ivory/80">
              Practical, community-centered projects — from the first well to long-term sanctuary planning. Every contribution goes toward work that centers the dignity of Congolese communities.
            </p>
          </Reveal>
        </div>
      </section>

      {/* PROJECT CARDS */}
      <section className="bg-ivory py-24 lg:py-32" data-testid="projects-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4" data-testid="projects-grid">
            {PROJECTS.map((p, i) => (
              <Reveal key={p.title} delay={(i % 4) * 0.07}>
                <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-hairline bg-white shadow-sm transition-shadow duration-300 hover:shadow-xl" data-testid={`project-card-${i + 1}`}>
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img src={p.img} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <h3 className="font-display text-xl font-semibold text-forest">{p.title}</h3>
                    <p className="mt-2.5 flex-1 text-sm leading-relaxed text-mutedink">{p.desc}</p>
                    <div className="mt-6">
                      <DonateButton variant="outlineDark" className="w-full !px-4 !text-xs" testid={`project-donate-${i + 1}`}>
                        {p.cta}
                      </DonateButton>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SANCTUARY DEEP DIVE */}
      <section id="sanctuary" className="scroll-mt-20 bg-sand py-24 lg:py-32" data-testid="sanctuary-programs-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading
            eyebrow="The Long-Term Vision"
            title="The Nseya Women & Children's Sanctuary"
            sub="A safe place to heal, learn, rebuild and thrive. Our long-term vision is to create a self-sustaining sanctuary for women and children affected by violence, displacement and vulnerability. The goal: an environment where people can move from immediate care toward long-term stability, skills and independence."
          />
          <div className="mt-14">
            <SanctuaryMasterplan />
          </div>

          {/* 13 COMPONENTS */}
          <div className="mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="sanctuary-components-grid">
            {SANCTUARY_COMPONENTS.map((c, i) => (
              <Reveal key={c.n} delay={(i % 3) * 0.06}>
                <div className="flex h-full gap-4 rounded-xl border border-hairline bg-white p-5" data-testid={`sanctuary-component-${c.n}`}>
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-forest font-mono text-xs font-semibold text-gold">{c.n}</span>
                  <div>
                    <h3 className="font-display text-base font-semibold text-forest">{c.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-mutedink">{c.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* SITE CONCEPT */}
          <Reveal delay={0.1}>
            <div className="mt-16 grid gap-8 rounded-2xl border border-hairline bg-white p-9 lg:grid-cols-12" data-testid="site-concept-card">
              <div className="lg:col-span-4">
                <p className="eyebrow text-ochre">Site Concept — Proposed</p>
                <p className="mt-4 font-display text-5xl font-semibold text-forest">4–5<span className="ml-2 text-xl font-medium text-mutedink">hectares</span></p>
                <p className="mt-4 text-sm leading-relaxed text-mutedink">
                  Potential location: near Lomami or Kasaï region, DRC. This is a proposed concept — not a confirmed or acquired site.
                </p>
              </div>
              <div className="lg:col-span-8">
                <p className="leading-relaxed text-mutedink">
                  The sanctuary concept is currently envisioned as a 4–5 hectare community. Site selection, partnerships, engineering, construction plans, budgets and timelines will be developed as the project progresses.
                </p>
                <p className="mt-4 leading-relaxed text-mutedink">
                  Sustainable &amp; affordable by design: modified shipping container homes, compressed earth blocks and locally sourced materials, solar energy and rainwater harvesting, permaculture and organic farming — low-cost, high-impact solutions.
                </p>
              </div>
            </div>
          </Reveal>

          {/* PILLARS */}
          <div className="mt-16">
            <SectionHeading center eyebrow="Self-Sustainability" title="More than a shelter. A community built to sustain itself." sub="Food grown locally. Water managed locally. Solar energy, vocational training, agriculture and enterprise creating skills, income and employment — with community spaces supporting connection and healing." />
            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-5" data-testid="pillars-grid">
              {PILLARS.map((p, i) => (
                <Reveal key={p.title} delay={i * 0.07}>
                  <div className="h-full rounded-2xl bg-forest p-7 text-ivory" data-testid={`pillar-${p.title.toLowerCase()}`}>
                    <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold">{String(i + 1).padStart(2, "0")}</p>
                    <h3 className="mt-4 font-display text-2xl font-semibold italic">{p.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-ivory/75">{p.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal className="mt-16">
            <div className="overflow-hidden rounded-2xl">
              <img src={IMG.wellnessGroup} alt="Community gathering" className="aspect-[21/9] w-full object-cover" loading="lazy" data-testid="sanctuary-community-image" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-forest py-24 text-center text-ivory" data-testid="programs-cta-section">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <Reveal>
            <h2 className="font-display text-3xl font-semibold sm:text-4xl lg:text-5xl">Help build the foundation.</h2>
            <p className="mt-5 text-lg text-ivory/80">Your support helps Nseya Foundation work toward clean water, safe spaces, community resources and our long-term sanctuary vision.</p>
            <div className="mt-10">
              <DonateButton variant="big" testid="programs-donate-button" />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
